import { useEffect, useState } from "react";
import { Cohort } from "../../model/cohort";
import { Stream, StudentGroup, StudentGroupMember } from "../../model/stream";
import { getGroupMembers, getStudentGroups, addGroupMember, removeGroupMember } from "../../service/student-group-service";
import { getStreams } from "../../service/stream-service";
import { getUserStudents } from "../../../-account/service/student-service";
import { User } from "../../../-account/model/user";
import { useToast } from "../../../../context/toast/toast-provider";
import {
    Button,
    Typography,
    List,
    ListItem,
    ListItemText,
    IconButton,
    Autocomplete,
    TextField,
    Paper,
    CircularProgress,
} from "@mui/material";
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PersonRemoveIcon from '@mui/icons-material/PersonRemove';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import commonStyles from "../../../style/common.module.css";

export default function CohortStreamGroupMembers({
    cohort,
    stream,
    group,
    onBack,
}: {
    cohort: Cohort,
    stream: Stream,
    group: StudentGroup,
    onBack: () => void,
}) {
    const [loading, setLoading] = useState(true);
    const [members, setMembers] = useState<StudentGroupMember[]>([]);
    const [allStreamMemberIds, setAllStreamMemberIds] = useState<number[]>([]);
    const [students, setStudents] = useState<User[]>([]);
    const [selectedStudent, setSelectedStudent] = useState<User | null>(null);
    const [adding, setAdding] = useState(false);

    const toast = useToast();

    useEffect(() => {
        loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [group.id]);

    const loadData = () => {
        setLoading(true);
        Promise.all([
            getGroupMembers(group.id!),
            getUserStudents(cohort.id),
            getStreams(cohort.id!),
        ]).then(([membersResult, studentsResult, streamsResult]) => {
            setMembers(membersResult ?? []);
            setStudents(studentsResult ?? []);
            const streams = streamsResult ?? [];
            Promise.all(streams.map(s => getStudentGroups(s.id!))).then(groupsResults => {
                const memberIds = groupsResults
                    .flatMap(groups => (groups ?? []))
                    .flatMap(g => g.membersList ?? [])
                    .map(m => m.studentId);
                setAllStreamMemberIds(memberIds);
                setLoading(false);
            });
        });
    }

    const availableStudents = () => {
        return students.filter(_ => !allStreamMemberIds.includes(_.id));
    }

    const handleAdd = () => {
        if (!selectedStudent) return;
        setAdding(true);
        const member: StudentGroupMember = {
            groupId: group.id!,
            studentId: selectedStudent.id,
        };
        addGroupMember(group.id!, member).then(() => {
            toast.addToast(`${selectedStudent.name} added to group`, "success");
            setSelectedStudent(null);
            setAdding(false);
            loadData();
        }).catch(() => {
            toast.addToast(`Unable to add member`, "error");
            setAdding(false);
        });
    }

    const handleRemove = (member: StudentGroupMember) => {
        removeGroupMember(group.id!, member.studentId).then(() => {
            toast.addToast(`${member.studentName} removed from group`, "success");
            loadData();
        }).catch(() => {
            toast.addToast(`Unable to remove member`, "error");
        });
    }

    if (loading) {
        return <CircularProgress />;
    }

    return (
        <div className={commonStyles.content}>
            <Button startIcon={<ArrowBackIcon />} onClick={onBack} sx={{ mb: 1 }}>
                Back to Groups
            </Button>
            <Typography variant="h6" sx={{ mb: 2 }}>
                Members of: {group.name} ({members.length}/10)
            </Typography>

            {members.length < 10 && (
                <Paper sx={{ p: 2, mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Autocomplete
                        sx={{ flex: 1 }}
                        options={availableStudents()}
                        getOptionLabel={(option) => `${option.name} (${option.email ?? ''})`}
                        value={selectedStudent}
                        onChange={(_, value) => setSelectedStudent(value)}
                        renderInput={(params) => <TextField {...params} label="Add student" size="small" />}
                    />
                    <Button
                        variant="contained"
                        startIcon={<PersonAddIcon />}
                        onClick={handleAdd}
                        disabled={!selectedStudent || adding}
                    >
                        Add
                    </Button>
                </Paper>
            )}

            <List>
                {members.map(member => (
                    <ListItem
                        key={member.id}
                        secondaryAction={
                            <IconButton edge="end" onClick={() => handleRemove(member)} color="error">
                                <PersonRemoveIcon />
                            </IconButton>
                        }
                    >
                        <ListItemText
                            primary={member.studentName}
                            secondary={member.studentEmail}
                        />
                    </ListItem>
                ))}
                {members.length === 0 && (
                    <ListItem>
                        <ListItemText primary="No members yet" />
                    </ListItem>
                )}
            </List>
        </div>
    );
}
